import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  X, Send, Sparkles, Trash2, Loader2, Bot, ArrowDown, Paperclip, Image as ImageIcon, FileText,
  Mic, MicOff, BarChart3, Search, ArrowRight
} from 'lucide-react';
import agentApi from '../../api/agent';
import { mediaApi } from '../../api/media';
import useAgentStore from '../../stores/agentStore';
import useUiStore from '../../stores/uiStore';
import AgentMessage, { StreamingProgress } from './AgentMessage';

// ── Quick Actions ──────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { icon: BarChart3, label: 'DNA Insights', path: '/dna' },
  { icon: FileText, label: 'Content Plan', path: '/blog?tab=plan' },
  { icon: Search, label: 'SEO prüfen', path: '/seo' },
  { icon: Sparkles, label: 'AI Studio', path: '/blog?tab=write' },
];

/**
 * AgentPanel — Modernes Agent-Chat-Panel (Slide-over von rechts)
 *
 * Features:
 * - SSE Streaming mit Live-Fortschritt
 * - Bild-Upload per Bueroklammer oder Drag & Drop
 * - Markdown-Rendering in Nachrichten
 * - Batch-Approve: Alle Aktionen mit einem Klick freigeben
 * - Auto-Continue: Agent setzt nach Freigabe automatisch fort
 */
export default function AgentPanel() {
  const navigate = useNavigate();
  const { agentPanelOpen, setAgentPanelOpen } = useUiStore();
  const {
    messages, addMessage, sessionId, setSessionId,
    pendingApprovals, addPendingApprovals, setPendingApprovals,
    removeApproval, moduleContext, clearChat, isLoading, setLoading,
    // Streaming
    isStreaming, streamingSteps, currentStep,
    startStreaming, stopStreaming, handleStreamEvent
  } = useAgentStore();

  const [input, setInput] = useState('');
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [attachment, setAttachment] = useState(null); // { mediaId, url, filename, localPreview }
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  // Audio Recording
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingError, setRecordingError] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);

  // Auto-Scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingSteps, scrollToBottom]);

  // Detect if user scrolled up
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    setShowScrollBtn(!atBottom);
  }, []);

  // Focus on open
  useEffect(() => {
    if (agentPanelOpen) {
      setTimeout(() => textareaRef.current?.focus(), 200);
    }
  }, [agentPanelOpen]);

  // Cleanup local preview URL on unmount
  useEffect(() => {
    return () => {
      if (attachment?.localPreview) URL.revokeObjectURL(attachment.localPreview);
    };
  }, [attachment]);

  // Cleanup audio recording on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // ── Bild-Upload ──────────────────────────────────────────────────────────

  const handleFileUpload = useCallback((file) => {
    if (!file) return;

    // Max 10 MB Check
    if (file.size > 10 * 1024 * 1024) {
      addMessage({ role: 'system', content: 'Datei ist zu gross (max. 10 MB).' });
      return;
    }

    const isImage = file.type.startsWith('image/');

    // Nur lokale Vorschau — Upload erst beim Senden
    const localPreview = isImage ? URL.createObjectURL(file) : null;
    setAttachment({ file, filename: file.name, localPreview, isImage, fileSize: file.size });
  }, [addMessage]);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    // Input zuruecksetzen damit gleiche Datei nochmal waehlbar ist
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [handleFileUpload]);

  const removeAttachment = useCallback(() => {
    if (attachment?.localPreview) URL.revokeObjectURL(attachment.localPreview);
    setAttachment(null);
  }, [attachment]);

  // ── Drag & Drop ──────────────────────────────────────────────────────────

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  // ── Speech Recognition (Web Speech API) ─────────────────────────────────

  const startRecording = useCallback(() => {
    setRecordingError(null);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setRecordingError('Spracheingabe wird von diesem Browser nicht unterstuetzt (Chrome/Edge empfohlen)');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'de-DE';
    recognition.continuous = true;
    recognition.interimResults = true;

    mediaRecorderRef.current = recognition;
    audioChunksRef.current = []; // Speichert finale Ergebnisse

    setRecordingTime(0);
    recordingTimerRef.current = setInterval(() => {
      setRecordingTime(t => t + 1);
    }, 1000);

    // Audio-Level Simulation basierend auf Interim-Ergebnissen
    let lastResultTime = Date.now();
    const updateLevel = () => {
      if (!mediaRecorderRef.current) return;
      const timeSince = Date.now() - lastResultTime;
      // Level faellt ab wenn nichts gesprochen wird
      setAudioLevel(prev => timeSince < 500 ? Math.min(80, prev + 20) : Math.max(0, prev - 5));
      animFrameRef.current = requestAnimationFrame(updateLevel);
    };
    updateLevel();

    recognition.onresult = (event) => {
      lastResultTime = Date.now();
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        audioChunksRef.current.push(finalTranscript);
      }
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed') {
        setRecordingError('Mikrofon-Zugriff verweigert — bitte in den Browser-Einstellungen erlauben');
      } else if (event.error === 'no-speech') {
        setRecordingError('Kein Sprechen erkannt — bitte nochmal versuchen');
      } else if (event.error !== 'aborted') {
        setRecordingError(`Spracheingabe-Fehler: ${event.error}`);
      }
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      setAudioLevel(0);
    };

    recognition.onend = () => {
      clearInterval(recordingTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      setAudioLevel(0);
      setIsRecording(false);

      const fullText = audioChunksRef.current.join(' ').trim();
      if (fullText) {
        setInput(prev => prev + (prev ? ' ' : '') + fullText);
      }
    };

    recognition.start();
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current) {
      try { mediaRecorderRef.current.stop(); } catch { /* already stopped */ }
    }
    setIsRecording(false);
    clearInterval(recordingTimerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    setAudioLevel(0);
  }, []);

  const formatRecordingTime = useCallback((secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }, []);

  // ── Streaming Chat senden ────────────────────────────────────────────────

  const handleSendStreaming = useCallback(async (userMessage) => {
    startStreaming();

    try {
      const result = await agentApi.chatStream(
        userMessage,
        { sessionId, context: moduleContext },
        handleStreamEvent
      );

      if (result) {
        setSessionId(result.sessionId);

        addMessage({
          role: 'assistant',
          content: result.message,
          toolResults: result.toolResults,
          pendingApprovals: result.pendingApprovals
        });

        if (result.pendingApprovals?.length > 0) {
          addPendingApprovals(result.pendingApprovals);
        }
      }
    } catch (err) {
      addMessage({
        role: 'system',
        content: `Fehler: ${err.message || 'Verbindung fehlgeschlagen'}`
      });
    } finally {
      stopStreaming();
    }
  }, [sessionId, moduleContext, startStreaming, stopStreaming, handleStreamEvent, setSessionId, addMessage, addPendingApprovals]);

  // ── Fallback: Klassischer Chat (ohne Streaming) ────────────────────────

  const chatMutation = useMutation({
    mutationFn: async (userMessage) => {
      const result = await agentApi.chat(userMessage, {
        sessionId,
        context: moduleContext
      });
      return result.data;
    },
    onMutate: () => setLoading(true),
    onSuccess: (data) => {
      setSessionId(data.sessionId);

      addMessage({
        role: 'assistant',
        content: data.message,
        toolResults: data.toolResults,
        pendingApprovals: data.pendingApprovals
      });

      if (data.pendingApprovals?.length > 0) {
        addPendingApprovals(data.pendingApprovals);
      }
    },
    onError: (err) => {
      addMessage({
        role: 'system',
        content: `Fehler: ${err.response?.data?.message || err.message || 'Verbindung fehlgeschlagen'}`
      });
    },
    onSettled: () => setLoading(false)
  });

  // ── Einzelne Approval ────────────────────────────────────────────────────

  const approveMutation = useMutation({
    mutationFn: ({ approvalId, approved }) =>
      agentApi.approve(approvalId, approved),
    onSuccess: (response, variables) => {
      const data = response.data;
      removeApproval(variables.approvalId);

      addMessage({
        role: 'system',
        content: data.rejected
          ? `Abgelehnt: ${data.message}`
          : data.error
            ? `Fehler: ${data.error}`
            : `Ausgefuehrt: ${data.tool}`,
        toolResults: data.result ? [{ tool: data.tool, result: data.result }] : undefined
      });
    }
  });

  // ── Batch Approve + Auto-Continue (mit Streaming) ──────────────────────

  const handleApproveAllStreaming = useCallback(async () => {
    if (!sessionId || isLoading) return;

    setPendingApprovals([]);
    startStreaming();

    try {
      const result = await agentApi.approveAllStream(
        sessionId,
        moduleContext,
        handleStreamEvent
      );

      if (result) {
        if (result.results?.length > 0) {
          addMessage({
            role: 'system',
            content: `${result.approved} Aktion${result.approved !== 1 ? 'en' : ''} ausgefuehrt`,
            toolResults: result.results
          });
        }

        if (result.followUp) {
          setSessionId(result.followUp.sessionId);
          addMessage({
            role: 'assistant',
            content: result.followUp.message,
            toolResults: result.followUp.toolResults,
            pendingApprovals: result.followUp.pendingApprovals
          });

          if (result.followUp.pendingApprovals?.length > 0) {
            addPendingApprovals(result.followUp.pendingApprovals);
          }
        }
      }
    } catch (err) {
      addMessage({
        role: 'system',
        content: `Batch-Freigabe fehlgeschlagen: ${err.message}`
      });
    } finally {
      stopStreaming();
    }
  }, [sessionId, isLoading, moduleContext, startStreaming, stopStreaming, handleStreamEvent, setPendingApprovals, addMessage, setSessionId, addPendingApprovals]);

  // Fallback Approve-All (ohne Streaming)
  const approveAllMutation = useMutation({
    mutationFn: async () => {
      const result = await agentApi.approveAll(sessionId, moduleContext);
      return result.data;
    },
    onMutate: () => setLoading(true),
    onSuccess: (data) => {
      setPendingApprovals([]);
      if (data.results?.length > 0) {
        addMessage({
          role: 'system',
          content: `${data.approved} Aktion${data.approved !== 1 ? 'en' : ''} ausgefuehrt`,
          toolResults: data.results
        });
      }
      if (data.followUp) {
        setSessionId(data.followUp.sessionId);
        addMessage({
          role: 'assistant',
          content: data.followUp.message,
          toolResults: data.followUp.toolResults,
          pendingApprovals: data.followUp.pendingApprovals
        });
        if (data.followUp.pendingApprovals?.length > 0) {
          addPendingApprovals(data.followUp.pendingApprovals);
        }
      }
    },
    onError: (err) => {
      addMessage({
        role: 'system',
        content: `Batch-Freigabe fehlgeschlagen: ${err.response?.data?.message || err.message}`
      });
    },
    onSettled: () => setLoading(false)
  });

  // ── Handlers ───────────────────────────────────────────────────────────

  const handleSend = useCallback(async () => {
    if (isLoading || isUploading) return;
    if (!input.trim() && !attachment) return;

    const userText = input.trim();
    const currentAttachment = attachment;
    let content = userText;
    let messageAttachment = null;

    // Sofort Input leeren + Attachment entfernen
    setInput('');
    setAttachment(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Falls Datei angehaengt: jetzt erst hochladen
    if (currentAttachment?.file) {
      setIsUploading(true);

      // User-Nachricht sofort anzeigen (mit Vorschau, Upload laeuft)
      addMessage({
        role: 'user',
        content: userText || 'Datei hochgeladen',
        attachment: {
          type: currentAttachment.isImage ? 'image' : 'file',
          filename: currentAttachment.filename,
          preview: currentAttachment.localPreview,
          uploading: true
        }
      });

      try {
        const result = await mediaApi.upload(currentAttachment.file);
        const media = result.data;

        const label = currentAttachment.isImage ? 'BILD' : 'DATEI';
        const fileContext = `[${label} HOCHGELADEN]\nDatei: ${currentAttachment.filename}\nWordPress Media ID: ${media.id}\nURL: ${media.source_url || media.link}\n\n`;
        content = userText ? fileContext + userText : fileContext + `Ich habe eine ${currentAttachment.isImage ? 'Bilddatei' : 'Datei'} hochgeladen.`;
      } catch (err) {
        addMessage({
          role: 'system',
          content: `Upload fehlgeschlagen: ${err.response?.data?.message || err.message}`
        });
        setIsUploading(false);
        return; // Abbrechen — kein Agent-Call
      } finally {
        setIsUploading(false);
        if (currentAttachment.localPreview) URL.revokeObjectURL(currentAttachment.localPreview);
      }
    } else {
      // Keine Datei — nur Text-Nachricht anzeigen
      if (!content) return;
      addMessage({ role: 'user', content });
    }

    handleSendStreaming(content);
  }, [input, isLoading, isUploading, attachment, addMessage, handleSendStreaming]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleApprove = useCallback((approvalId) => {
    approveMutation.mutate({ approvalId, approved: true });
  }, [approveMutation]);

  const handleReject = useCallback((approvalId) => {
    approveMutation.mutate({ approvalId, approved: false });
  }, [approveMutation]);

  const handleApproveAll = useCallback(() => {
    handleApproveAllStreaming();
  }, [handleApproveAllStreaming]);

  if (!agentPanelOpen) return null;

  const suggestions = [
    'Welche Seiten gibt es aktuell?',
    'Analysiere die SEO-Performance',
    'Was weißt du über unser Unternehmen?',
    'Zeig mir den Content-Plan'
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 backdrop-blur-[1px]"
        onClick={() => setAgentPanelOpen(false)}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] max-w-[95vw] bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700 z-50 flex flex-col shadow-2xl">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-950/30 dark:via-purple-950/20 dark:to-pink-950/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                amplione Agent
              </h2>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {isUploading
                  ? 'Datei wird hochgeladen...'
                  : isStreaming && currentStep
                    ? currentStep
                    : isLoading
                      ? 'Arbeitet...'
                      : pendingApprovals.length > 0
                        ? `${pendingApprovals.length} Aktion${pendingApprovals.length !== 1 ? 'en' : ''} warten`
                        : moduleContext?.module
                          ? `Kontext: ${moduleContext.module}`
                          : 'Bereit'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title="Chat leeren"
              >
                <Trash2 size={15} />
              </button>
            )}
            <button
              onClick={() => setAgentPanelOpen(false)}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <X size={17} />
            </button>
          </div>
        </div>

        {/* ── Chat Area (Drag & Drop Zone) ─────────────────────────────── */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex-1 overflow-y-auto px-5 py-4 scroll-smooth relative transition-colors ${
            isDragging ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''
          }`}
        >
          {/* Drag Overlay */}
          {isDragging && (
            <div className="absolute inset-4 border-2 border-dashed border-indigo-400 dark:border-indigo-500 rounded-2xl flex items-center justify-center bg-indigo-50/80 dark:bg-indigo-900/30 z-10 pointer-events-none">
              <div className="text-center">
                <Paperclip className="w-10 h-10 text-indigo-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">Datei hier ablegen</p>
              </div>
            </div>
          )}

          {messages.length === 0 && !isStreaming ? (
            /* ── Empty State ──────────────────────────────────────────── */
            <div className="flex flex-col items-center justify-center h-full text-center px-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center mb-5 shadow-sm">
                <Bot className="w-8 h-8 text-indigo-500 dark:text-indigo-400" />
              </div>
              <p className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Wie kann ich helfen?
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mb-6 max-w-xs leading-relaxed">
                Ich kann WordPress-Seiten erstellen, SEO optimieren, Content generieren und deine Website verwalten.
              </p>
              <div className="grid gap-2 w-full max-w-sm">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => {
                      addMessage({ role: 'user', content: suggestion });
                      handleSendStreaming(suggestion);
                    }}
                    className="text-left text-sm px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-all"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            /* ── Messages ─────────────────────────────────────────────── */
            <>
              {messages.map((msg, i) => (
                <AgentMessage
                  key={i}
                  message={msg}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  onApproveAll={handleApproveAll}
                />
              ))}

              {/* Streaming Progress */}
              {isStreaming && streamingSteps.length > 0 && (
                <StreamingProgress steps={streamingSteps} currentStep={currentStep} />
              )}

              {/* Fallback Loading Animation */}
              {isLoading && !isStreaming && (
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex items-center gap-1.5 px-3 py-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-indigo-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-sm text-gray-400 dark:text-gray-500 ml-2">
                      Agent denkt nach...
                    </span>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Scroll-to-bottom button */}
        {showScrollBtn && messages.length > 0 && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-20 right-6 w-8 h-8 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowDown size={14} className="text-gray-500" />
          </button>
        )}

        {/* ── Recording / Transcribing Indicator ─────────────────────── */}
        {(isRecording || isTranscribing) && (
          <div className="px-5 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
              <span className="text-xs font-medium text-red-600 dark:text-red-400">
                {isRecording ? `Aufnahme ${formatRecordingTime(recordingTime)}` : 'Transkribiere...'}
              </span>
              {isRecording && (
                <>
                  <div className="flex-1 h-2 bg-red-100 dark:bg-red-900/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-red-400 dark:bg-red-500 rounded-full transition-all duration-75"
                      style={{ width: `${audioLevel}%` }}
                    />
                  </div>
                  <button
                    onClick={stopRecording}
                    className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium shrink-0"
                  >
                    Stoppen
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Recording Error */}
        {recordingError && !isRecording && !isTranscribing && (
          <div className="px-5 py-2 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800 flex items-center gap-2">
            <span className="text-xs text-amber-700 dark:text-amber-400 flex-1">{recordingError}</span>
            <button
              onClick={() => { setRecordingError(null); startRecording(); }}
              className="text-xs text-amber-700 dark:text-amber-400 hover:text-amber-900 font-medium shrink-0"
            >
              Nochmal
            </button>
            <button
              onClick={() => setRecordingError(null)}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* ── Input Area ──────────────────────────────────────────────── */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3 bg-gray-50/50 dark:bg-gray-800/30">
          {/* Attachment Preview */}
          {attachment && (
            <div className="flex items-center gap-3 mb-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
              {attachment.localPreview ? (
                <img
                  src={attachment.localPreview}
                  alt={attachment.filename}
                  className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <FileText size={20} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                  {attachment.filename}
                </p>
                <p className="text-[10px] text-gray-400">
                  {attachment.fileSize ? `${(attachment.fileSize / 1024).toFixed(0)} KB — wird beim Senden hochgeladen` : 'Bereit'}
                </p>
              </div>
              <button
                onClick={removeAttachment}
                className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* Input Row */}
          <div className="flex items-end gap-2">
            {/* Hidden File Input */}
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Paperclip Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || isUploading || isRecording}
              className="p-2.5 text-gray-400 hover:text-indigo-500 rounded-xl hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Datei hochladen"
            >
              <Paperclip size={16} />
            </button>

            {/* Mic Button */}
            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isTranscribing || isLoading}
              className={`p-2.5 rounded-xl transition-colors ${
                isRecording
                  ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400 animate-pulse'
                  : isTranscribing
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20'
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              title={isRecording ? 'Aufnahme stoppen' : isTranscribing ? 'Transkribiere...' : 'Spracheingabe'}
            >
              {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
            </button>

            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                const ta = e.target;
                ta.style.height = 'auto';
                ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
              }}
              onKeyDown={handleKeyDown}
              placeholder={attachment ? 'Beschreibe die Datei oder was der Agent damit tun soll...' : 'Nachricht an den Agent...'}
              rows={1}
              className="flex-1 resize-none text-sm px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
              disabled={isLoading || isTranscribing}
            />

            {/* Send Button */}
            <button
              onClick={handleSend}
              disabled={(!input.trim() && !attachment?.file) || isLoading || isUploading}
              className="p-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow"
            >
              <Send size={16} />
            </button>
          </div>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5 text-center">
            Enter zum Senden · Shift+Enter für Zeilenumbruch · Dateien per Drag & Drop
          </p>
        </div>

        {/* ── Quick Actions ──────────────────────────────────────────── */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-3">
          <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 mb-2 uppercase tracking-wider">Schnellzugriff</p>
          <div className="grid grid-cols-2 gap-1.5">
            {QUICK_ACTIONS.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.path}
                  onClick={() => {
                    navigate(action.path);
                    setAgentPanelOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <Icon size={13} />
                  {action.label}
                  <ArrowRight size={10} className="ml-auto opacity-40" />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
